import { Box, Chip, Typography, Divider, Button } from "@mui/joy";
import { useEffect } from "react";
import { Clock, PackageCheck, FilePlus, RotateCw } from "lucide-react";
import moment from "moment";

import TaskActionComponent from "../../../components/Tasks/components/TaskAction";
import { DocumentPreviewWidget, type IMenuAction } from "../../../components";
import { useMaterialOrdering } from "../hooks";
import { OrderSummary } from "./OrderSummary";
import { useDocument } from "../../../hooks/query";
import { useProject } from "../../../hooks";
import { useNotification } from "../../../hooks/useNotification";

interface ITaskComponent {
    activeTask: any;
    onComplete: any;
    actionProgress: any;
}

function SupplyOrderWidget({
    documents,
    handleGenerateSupplyOrder,
    isGenerating,
}: {
    documents: any;
    handleGenerateSupplyOrder: () => void;
    isGenerating: boolean;
}) {
    const supplyOrderDoc = documents?.["SUPPLY_ORDER"];
    const isGenerated = supplyOrderDoc?.DocumentAssetId && supplyOrderDoc?.Status === "GENERATED";

    const menuActions: IMenuAction[] = [];

    if (isGenerated) {
        menuActions.push({
            label: "Regenerate",
            icon: <RotateCw size={16} />,
            onClick: handleGenerateSupplyOrder,
            disabled: isGenerating,
        });
    }

    return (
        <DocumentPreviewWidget
            title="Supply Order"
            description="Contains a  list of materials required for the project."
            thumbnailUrl={supplyOrderDoc?.thumbnailUrl}
            status={supplyOrderDoc?.Status || "NOT GENERATED"}
            assetId={supplyOrderDoc?.DocumentAssetId}
            menuActions={menuActions}
        />
    );
}

export default function TaskComponent(props: ITaskComponent) {
    const { activeTask, onComplete, actionProgress } = props;
    const { CreatedAt, Status } = activeTask;
    const { documents, refreshDocuments } = useMaterialOrdering();
    const { project } = useProject();
    const { useSupplyOrderCreate, useWorkOrderCreate } = useDocument();
    const { showSuccess, showError } = useNotification();
    const createSupplyOrderSvc = useSupplyOrderCreate();
    const createWorkOrderSvc = useWorkOrderCreate();

    const handleGenerateSupplyOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await createSupplyOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Supply order generated successfully");
            // Refresh documents after generation
            setTimeout(() => {
                refreshDocuments();
            }, 1000);
        } catch (error) {
            console.error("Error generating supply order:", error);
            showError("Failed to generate supply order");
        }
    };

    const handleGenerateWorkOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await createWorkOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Work order generated successfully");
            // Refresh documents after generation
            setTimeout(() => {
                refreshDocuments();
            }, 1000);
        } catch (error) {
            console.error("Error generating work order:", error);
            showError("Failed to generate work order");
        }
    };

    useEffect(() => {
        const scrollableDiv = document.getElementById("project-details-parent");
        if (scrollableDiv) {
            //scrollableDiv.scrollTop = 0; // or use scrollTo
            scrollableDiv.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, []);

    return (
        <Box>
            <Box mt={2} ml={1} mr={1}>
                <Typography startDecorator={<PackageCheck size={16} />} level="body-sm" gutterBottom>
                    Task
                </Typography>
                <Typography level="h4" sx={{ fontWeight: 500 }}>
                    Material Ordering
                </Typography>

                <Box mt={2} display={"flex"} gap={1}>
                    <Chip
                        color={Status === "COMPLETED" ? "success" : "warning"}
                        sx={{ fontSize: 12, border: "1px solid", borderColor: "divider" }}
                    >
                        {Status}
                    </Chip>
                    <Chip color="neutral" variant="outlined" sx={{ fontSize: 12 }} startDecorator={<Clock size={14} />}>
                        Created On: {moment(CreatedAt).format("MM/DD/YYYY")}
                    </Chip>
                </Box>

                <Box mt={3}>
                    <Box flex={1}>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Description
                        </Typography>
                        <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                            The primary goal of the Material Ordering stage is to ensure the timely procurement of
                            necessary materials for the project. This stage involves identifying required materials,
                            verifying quantities, and coordinating with suppliers to prevent delays. By streamlining the
                            ordering process, it ensures that roofing repairs, maintenance, or installations proceed
                            smoothly without interruptions due to material shortages.
                        </Typography>
                    </Box>
                </Box>

                <Box mt={2}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Order Summary
                        </Typography>
                        <Divider sx={{ mt: 1, mb: 2 }} />
                        <Box display={"flex"} flexDirection={"column"} gap={2}>
                            <OrderSummary />
                        </Box>
                    </Box>
                </Box>
                <Box mt={2}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Document Review
                        </Typography>
                        <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                            Material quantities are automatically calculated based on the roof area and other project
                            factors. To maintain accurate estimations, manual adjustments are not recommended.
                        </Typography>
                    </Box>
                    <Box mt={2} display={"flex"} gap={1} flexWrap={"wrap"}>
                        <Box sx={{ position: "relative", width: 230, height: 280 }}>
                            <SupplyOrderWidget
                                documents={documents}
                                handleGenerateSupplyOrder={handleGenerateSupplyOrder}
                                isGenerating={createSupplyOrderSvc.isPending}
                            />
                            {(!documents?.["SUPPLY_ORDER"]?.DocumentAssetId ||
                                documents?.["SUPPLY_ORDER"]?.Status === "NOT GENERATED" ||
                                documents?.["SUPPLY_ORDER"]?.Status === "INIT") && (
                                <Button
                                    startDecorator={<FilePlus size={16} />}
                                    size="sm"
                                    variant="solid"
                                    color="primary"
                                    loading={createSupplyOrderSvc.isPending}
                                    onClick={handleGenerateSupplyOrder}
                                    sx={{
                                        position: "absolute",
                                        bottom: 24,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        zIndex: 10,
                                        minWidth: 180,
                                    }}
                                >
                                    Generate
                                </Button>
                            )}
                        </Box>
                        <DocumentPreviewWidget
                            title="Gutter Order"
                            description="Includes material specifications for gutter components."
                            thumbnailUrl={documents?.["GUTTER_ORDER"]?.thumbnailUrl}
                            status={documents?.["GUTTER_ORDER"]?.Status}
                            assetId={documents?.["GUTTER_ORDER"]?.DocumentAssetId}
                        />
                        {(() => {
                            const workOrderDoc = documents?.["WORK_ORDER"];
                            const isGenerated = workOrderDoc?.DocumentAssetId && workOrderDoc?.Status === "GENERATED";
                            const menuActions: IMenuAction[] = [];

                            if (isGenerated) {
                                menuActions.push({
                                    label: "Regenerate",
                                    icon: <RotateCw size={16} />,
                                    onClick: handleGenerateWorkOrder,
                                    disabled: createWorkOrderSvc.isPending,
                                });
                            }

                            return (
                                <DocumentPreviewWidget
                                    title="Work Order"
                                    description="Outlines the materials needed to complete the assigned work."
                                    thumbnailUrl={workOrderDoc?.thumbnailUrl}
                                    status={workOrderDoc?.Status || "NOT GENERATED"}
                                    assetId={workOrderDoc?.DocumentAssetId}
                                    menuActions={menuActions}
                                />
                            );
                        })()}
                    </Box>
                </Box>

                <TaskActionComponent
                    taskName={"Material Ordering"}
                    onComplete={onComplete}
                    actionProgress={actionProgress}
                    isNextEligible={true}
                />
            </Box>
        </Box>
    );
}
